/* eslint-disable no-param-reassign */
/* eslint-disable max-len */
import isMobile from 'utils/isMobile';
import { trackers } from '@rmm/error-tracker';
import { detectAnyAdblocker } from 'just-detect-adblock';
import UserPreferences from './UserPreferences';
import custParamsUrl from '../../js/a9/custParamsUrl';
import { updateFreewheelUrl } from '../../js/utils/freewheelUtils';
import './anvato-player.scss';

const { SimpleErrorTracker } = trackers;

/**
 * A component that manages behavior of an individual Anvato video player.
 *
 * Anvato docs: https://dev.anvato.net/api/playerv3
 */
export default class AnvatoPlayer {
  /**
   * Categories of the video in the player.
   *
   * @type {Array}
   */
  categories = [];

  /**
   * Comma-separated string of tags assigned to the video in the player.
   *
   * @type {string}
   */
  tags = '';

  /**
   * The context in which the Anvato player is displayed.
   *
   * @type {string}
   */
  pageType = '';

  /**
   * Whether the video should autoplay on page load.
   *
   * @type {Boolean}
   */
  autoplay = false;

  /**
   * Constructor.
   *
   * @param {Object} anvInstance Anvato player object for the video.
   * @param {BobPixel} bobPixel BobPixel instance.
   * @param {Object} pageAttributes Theme page attributes.
   */
  constructor(anvInstance, bobPixel, pageAttributes) {
    this.anvInstance = anvInstance;
    this.bobPixel = bobPixel;
    this.pageAttributes = pageAttributes || {};
    this.pageType = this.pageAttributes.page_type || '';
    this.userPreferences = new UserPreferences(this.anvInstance);
    this.listener = this.listener.bind(this);
    this.anvInstance.setListener(this.listener);
    this.anvInstance.on('beforeVideoLoad', (mediaInfo, initConfig) => addVideoContentLength(initConfig, mediaInfo));
  }

  /**
   * Calls the passed function on the Anvato player instance where that function
   * requires a callback, and returns a Promise that resolves when the player
   * returns the data passed to the callback.
   *
   * For example:
   *
   * this.promiseAnvFunc('getTitle').then((title) => doSomething(title));
   *
   * rather than:
   *
   * this.anvInstance.getTitle((value) => doSomething(title));
   *
   * @param {String} func Function to call on the player instance.
   * @returns {Promise<any>}
   */
  promiseAnvFunc(func) {
    return new Promise((resolve, reject) => {
      // Check that the function exists on the player.
      if (! this.anvInstance[func]) {
        reject(func);
      }

      // However, does not verify that the function actually expects a callback.
      this.anvInstance[func]((value) => resolve(value));
    });
  }

  /**
   * Listens to all events emitted by the player and controls responses to them
   *
   * @param {Object} event Anvato event object.
   */
  listener(event) {
    if (! event || ! event.name) {
      return;
    }

    /**
     * Only one function can be added as the player event listener. This
     * CustomEvent allows other functionality to receive notifications of
     * native Anvato events without the functionality having to be added to
     * this class.
     *
     * @todo The BoB functionality below could be split into its own component
     *       that uses the CustomEvent.
     */
    try {
      const nsAnvatoPlayerEvent = new CustomEvent(
        'nsAnvatoPlayerEvent', {
          detail: {
            name: event.name,
            pInstance: this.anvInstance.mergedConfig.pInstance,
            time: event.time,
            args: event.args,
          },
        },
      );

      window.dispatchEvent(nsAnvatoPlayerEvent);
    } catch (err) {
      // Do Nothing
    }

    switch (event.name) {
      case 'PLAYING_START':
        new SimpleErrorTracker('playbackStart', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;
      case 'VIDEO_STARTED':
        new SimpleErrorTracker('videoStart', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;
      case 'BUFFER_START':
        new SimpleErrorTracker('bufferStart', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;
      case 'BUFFER_END':
        new SimpleErrorTracker('bufferEnd', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;
      case 'VAST_REQUESTED':
        new SimpleErrorTracker('adRequested', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;
      case 'AD_BREAK_STARTED':
        new SimpleErrorTracker(`${0 === event.args?.[0] ? 'preroll' : 'postroll'}Started`, 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;
      case 'AD_BREAK_COMPLETED':
        new SimpleErrorTracker(`${0 === event.args?.[0] ? 'preroll' : 'postroll'}Completed`, 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;
      case 'AD_SKIPPED':
        // eslint-disable-next-line no-case-declarations
        let currentTime;
        this.anvInstance.getCurrentTime((time) => {
          currentTime = time;
        });
        new SimpleErrorTracker(`adSkipped${1 > currentTime ? 'Preroll' : 'Postroll'}`, 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;

      case 'PLAYLIST_UPDATED':
        new SimpleErrorTracker('updatePlayer', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();
        break;

      // There is no native event triggered when end screen(recommendation screen) is rendered.
      // This workaround checks for recommendation screen when BEFORE_VIDEO_LOAD gets triggered via
      // Query selectors.
      case 'BEFORE_VIDEO_LOAD':
        // eslint-disable-next-line no-case-declarations
        const player = document.querySelector(`#${this.anvInstance.config?.pInstance}`)?.contentDocument;
        if (player && 'visible' === player.querySelector(`#${this.anvInstance.config?.pInstance}-recommendation`)?.style?.visibility) {
          new SimpleErrorTracker('renderEndScreen', 100, {
            id: this.anvInstance.config?.pInstance,
            position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
            renderGroup: 'Akta internal',
          }).sendSuccess();
        }
        break;

      case 'METADATA_LOADED':
        new SimpleErrorTracker('metadataLoaded', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();

        /**
         * The METADATA_LOADED event is the first opportunity we know of to get
         * the categories and tags of the current video. No player API method
         * seems to expose them.
         *
         * Categories and tags are necessary to construct the keywords sent to
         * the BoB tag. We have these after METADATA_LOADED. Experience suggests
         * that the other player methods required to populate the BoB tag are
         * also available by the time METADATA_LOADED occurs. Therefore, it
         * should be safe to update the BoB pixel for the first time.
         */
        if (event.args) {
          this.categories = event.args[2].categories;
          this.tags = event.args[2].tags;
          this.updateBobPixel({
            vid_cr: 0,
          });
        }

        try {
          /**
           * We can cautiously assume that within this event the value will be
           * available. Using a try/catch only as a fail safe.
           */
          this.autoplay = this.anvInstance.mergedConfig.autoplay;
        } catch (err) {
          // Do nothing. `autoplay` will remain false.
        }

        break;

      case 'AUTOPLAY_STATUS':
        if (this.autoplay) {
          if ('MUTED' === event.args[0]) {
            /**
             * Firefox has a 'MUTED' autoplay policy. It also appears to be the
             * only browser that will throw an error in the player script if
             * unmuted autoplay is attempted. For this reason, we avoid applying
             * the users mute preference.
             */
            this.anvInstance.mute();
          } else {
            this.userPreferences.setMutePreference();
          }
        }
        break;

      case 'AD_STARTED':
        new SimpleErrorTracker('adStart', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();

        /**
         * Update the BoB pixel when an ad starts in the player.
         *
         * For the duration, pass the duration of the ad.
         */
        this.promiseAnvFunc('getAdDuration').then((adDuration) => {
          this.updateBobPixel({
            vid_duration: `:${adDuration}`,
            vid_type: 'pre-roll',
            vid_cr: 0,
          });
        });
        break;

      case 'AD_COMPLETED':
        /**
         * Update the BoB pixel when an ad completes in the player.
         *
         * Completion rate refers to the percent completion of the ad.
         */
        this.updateBobPixel({
          vid_type: 'pre-roll',
          vid_cr: 100,
        });
        break;

      case 'VIDEO_COMPLETED':
        new SimpleErrorTracker('videoCompleted', 100, {
          id: this.anvInstance.config?.pInstance,
          position: this.anvInstance.config?.plugins?.segmentCustom?.playerName,
          renderGroup: 'Akta internal',
        }).sendSuccess();

        /**
         * Update the BoB pixel when the video completes.
         */
        this.updateBobPixel({
          vid_cr: 100,
        });
        break;

      case 'USER_MUTE':
        /**
         * As of writing, while Safari and Firefox fire this event upon
         * each page load, Chrome does not. As a result, only Chrome will
         * honor the user's autoplay preference.
         *
         * See NXS-2316.
         *
         * @todo consider re-evaluating these criteria in NXS-1294.
         */
        if (this.autoplay) {
          this.userPreferences.setUserPreference(
            this.userPreferences.mutePrefKey,
            this.userPreferences.eventNameMute,
          );
        }
        break;

      case 'USER_UNMUTE':
        /**
         * See NXS-2316.
         *
         * @todo consider re-evaluating these criteria in NXS-1294.
         */
        if (this.autoplay) {
          this.userPreferences.setUserPreference(
            this.userPreferences.mutePrefKey,
            this.userPreferences.eventNameUnMute,
          );
        }
        break;

      case 'CAPTION_DETECTED':
        /*
         * Set the caption style directly by entering the number from each
         * enum in the `AnvatoPlayer.CaptionStyle` window object.
         * Normally, these numbers could be accessed via `window.AnvatoPlayer`
         * set in `anvload.js` but we do not have access to it in our
         * current implementation.
         *
         * Reference https://dev.anvato.net/api/playerv3#type-definitions
         * See NXS-2580
         */
        this.anvInstance.setCaptionStyle({
          override: true,
          bgAlpha: 3, // PERCENT_75.
          textColor: 0, // WHITE.
          fontType: 0, // ARIAL.
          fontSize: 2, // Default is `3`
          textEdgeStyle: 4, // UNIFORM.
          presetLevel: 3, // PERCENT_75.
        });
        break;

      default:
        break;
    }
  }

  /**
   * Call the BoB pixel for the video.
   *
   * For general specifcations, see LAK-197.
   *
   * @param {Object} args Custom arguments or overrides to include with the
   *   pixel.
   */
  updateBobPixel(args) {
    /**
     * The BoB pixel requires many pieces of data about the Anvato video before
     * it can be called. These are gathered incrementally, owing to the player's
     * reliance on callbacks.
     */

    // vid_keywords appears to be a combination of tags and categories.
    const keywords = [];

    if (this.tags) {
      keywords.push(this.tags);
    }

    if (this.categories) {
      keywords.push(...this.categories);
    }

    /**
     * mergedConfig appears to be the "final" player configuration taking all
     * possible sources into account. See
     * https://dev.anvato.net/api/player#configuration.
     */
    const {
      mergedConfig: {
        autoplay,
        nxs: {
          mp4Url = '',
        } = {},
      } = {},
    } = this.anvInstance;

    const defaults = {
      // The current URL.
      vid_pageurl: this.pageAttributes.page_url,
      // The keywords derived earlier.
      vid_keywords: keywords.join(','),
      // How the player was initiated. Derived from 'autoplay' on the config.
      vid_playinit: autoplay ? 'Auto' : 'Manual',
      // The video type. One of video, pre-roll.
      vid_type: 'video',
      // The video asset URL.
      vid_asset_url: mp4Url,
    };

    const variables = {
      ...defaults,
      ...args,
    };

    Promise.all(
      // Gather all the data from the player.
      [
        this.promiseAnvFunc('getTitle'),
        this.promiseAnvFunc('getDescription'),
        this.promiseAnvFunc('getWidth'),
        this.promiseAnvFunc('getHeight'),
        this.promiseAnvFunc('getDuration'),
      ],
    ).then(({
      0: title,
      1: description,
      2: width,
      3: height,
      4: seconds,
    }) => {
      variables.vid_title = title;
      variables.vid_desc = description;
      variables.vid_dim = `${width}x${height}`;

      const duration = new Date(seconds * 1000);
      // Videos longer than an hour not yet supported.
      // eslint-disable-next-line max-len
      variables.vid_duration = `${duration.getMinutes()}:${duration.getSeconds()}`;

      // At long last, update the BoB pixel.
      this.bobPixel.updatePixel(variables);
    });
  }
}

let isVideoPage;
let anvatoWrapper;
let anvatoIframe;
let firstTimeVisibilityCheck = false;
let bannerSize;
let anvatoPlayerFloatEvent = {};
let anvatoPlayerUnFloatEvent = {};
let playing = false;

// We want this to be specifically true or false to avoid an undefined warning.
/* eslint-disable-next-line no-unneeded-ternary */
const isHeroVideoFloat = document.querySelector('#daily-news-video')
  || document.querySelector('#big-story-video')
  || document.querySelector('#big-story-video-new')
  || document.querySelector('#daily-news-main-video')
  /* eslint-disable-next-line no-unneeded-ternary */
  ? true : false;

// These values are going to be called by an external script on analytics side.
window.anvatofloatCount = 0;
window.anvatoUnfloatTrigger = null;
window.anvatoVideoTitle = '';
window.anvatoVideoAssetId = '';

/**
 * Check if floating player listener should be activated.
 */
function checkFloatingPlayerActivation() {
  /**
   * If we do have an anvato iframe, but enable floating player is not true, then we remove the event listener.
   * Must check for anvatoiframe first because this function is also used for OVP player which causes it to error.
   */
  if (anvatoIframe?.id && ! window.anvp[anvatoIframe.id]?.config?.nxs?.enableFloatingPlayer) {
    document.removeEventListener('scroll', checkFloatingPlayerActivation);
    return;
  }
  const rect = anvatoWrapper.getBoundingClientRect();
  const middlePoint = (rect.bottom + rect.top) / 2;
  if (middlePoint < window.innerHeight && 0 < middlePoint) {
    document.addEventListener('scroll', floatingPlayerListener);
    document.removeEventListener('scroll', checkFloatingPlayerActivation);
    firstTimeVisibilityCheck = true;
  }
}

/**
 * Listener function which is called when page is scrolled.
 */
function floatingPlayerListener() {
  const isFloating = 'static' === anvatoWrapper.style.position;
  const rect = anvatoWrapper.getBoundingClientRect();
  /**
   * bannerSize varies depending on player size. The lower bannerSize value is,
   * the earlier the player will start to float
   */
  if (isMobile()) {
    bannerSize = 35;
  } else {
    bannerSize = isHeroVideoFloat ? 25 : 70;
  }
  const isVisible = rect.top < window.innerHeight && anvatoWrapper.offsetHeight <= (bannerSize + rect.bottom);

  if (isFloating && isVisible) {
    makeAnvatoFixed(anvatoWrapper, anvatoIframe, 'automatic');
  } else if (playing && ! isFloating && ! isVisible) {
    makeAnvatoFloating(anvatoWrapper, anvatoIframe);
  }
}

/**
 * Check if floating player listener should be activated.
 *
 * @param {Element} wrapper Anvato player's wrapper on the page
 * @param {Element} iframe Anvato iframe displaying the video
 *
 */
function createTitleBar(wrapper, iframe) {
  const titleClassName = isMobile() ? 'mobile-video-title-bar' : 'desktop-video-title-bar';
  const titleExist = wrapper.querySelector(`.${titleClassName}`);

  if (titleExist) {
    return;
  }

  const elemTitleBar = document.createElement('div');
  const elemTitleSpan = document.createElement('span');
  const elemCrossSign = document.createElement('div');
  elemTitleBar.classList.add(titleClassName);
  elemTitleSpan.classList.add('video-title-span');
  elemTitleSpan.innerHTML = isVideoPage
    ? document.querySelector('.video-title__text').innerHTML
    : iframe.contentWindow.document.querySelector('.anv-splash-title').innerHTML;
  elemCrossSign.classList.add('floating-close-button');
  elemCrossSign.innerHTML = '&#10005;';

  elemCrossSign.addEventListener('click', () => {
    window.anvp[iframe.id].pause();
    makeAnvatoFixed(wrapper, iframe, 'manual');
  });

  elemTitleBar.appendChild(elemTitleSpan);
  elemTitleBar.appendChild(elemCrossSign);

  // phpcs:ignore WordPressVIPMinimum.JS.HTMLExecutingFunctions.insertBefore
  wrapper.insertBefore(elemTitleBar, iframe);
}

/**
 * Makes Anvato player floating on desktop and mobile.
 *
 * @param {Element} wrapper Anvato player's wrapper on the page
 * @param {Element} iframe Anvato iframe displaying the video
 *
 */
function makeAnvatoFloating(wrapper, iframe) {
  // Fail-salfe to prevent the player from floating when it's not supposed to.
  if (anvatoIframe?.id && ! window.anvp[anvatoIframe.id]?.config?.nxs?.enableFloatingPlayer) {
    return;
  }

  window.anvatofloatCount += 1;

  anvatoPlayerFloatEvent = new CustomEvent(
    'anvatoPlayerFloat', {
      detail: {
        floatCount: window.anvatofloatCount, // counter starts at 1; increment for every subsequent event
        videoTitle: window.anvatoVideoTitle,
        videoAssetId: window.anvatoVideoAssetId,
      },
    },
  );

  window.dispatchEvent(anvatoPlayerFloatEvent);

  const rect = wrapper.getBoundingClientRect();

  wrapper.style.width = `${rect.width}px`;
  wrapper.style.height = `${rect.height}px`;
  iframe.style = '';
  createTitleBar(wrapper, iframe);
  wrapper.style.setProperty('position', 'static', 'important');

  if (isMobile()) {
    iframe.classList.add('mobile-floating-player');
  } else {
    iframe.classList.add('desktop-floating-player');
  }
  if (! isVideoPage) {
    iframe.contentWindow.document.querySelector('.anv-splash-title')
      .style.display = 'none';
  }
}

/**
 * Makes Anvato player fixed on desktop and mobile.
 *
 * @param {Element} wrapper Anvato player's wrapper on the page
 * @param {Element} iframe Anvato iframe displaying the video
 *
 */
function makeAnvatoFixed(wrapper, iframe, mode = '') {
  if (! iframe.classList.contains('mobile-floating-player')
    && ! iframe.classList.contains('desktop-floating-player')
  ) {
    return;
  }

  const mobileTitleBar = wrapper.querySelector('.mobile-video-title-bar');
  const desktopTitleBar = wrapper.querySelector('.desktop-video-title-bar');
  window.anvatoUnfloatTrigger = mode;

  anvatoPlayerUnFloatEvent = new CustomEvent(
    'anvatoPlayerUnFloat', {
      detail: {
        unFloatTrigger: window.anvatoUnfloatTrigger,
        videoTitle: window.anvatoVideoTitle,
        videoAssetId: window.anvatoVideoAssetId,
      },
    },
  );

  window.dispatchEvent(anvatoPlayerUnFloatEvent);

  wrapper.style = '';
  iframe.style = (isVideoPage || isHeroVideoFloat) ? '' : 'position: relative;';
  iframe.classList.remove('mobile-floating-player');
  iframe.classList.remove('desktop-floating-player');

  if (mobileTitleBar) {
    mobileTitleBar.remove();
  }

  if (desktopTitleBar) {
    desktopTitleBar.remove();
  }
  while (wrapper) {
    if (wrapper.classList.contains('article-header')) {
      return;
    }
    wrapper = wrapper.parentElement;
  }
  deleteTitleOverlay(wrapper, iframe);
}

/**
 * Listens to Anvato events and does the necessary operations for floating player.
 *
 * @param {Event} eventDetail Details of an event which is fired by Anvato player
 *
 */
function eventHandler(eventDetail) {
  const pagesWithFloatingBehaviour = ['video', 'article', 'home page', 'category'];
  if (! pagesWithFloatingBehaviour.includes(window.NXSTdata.content.pageType)) {
    return;
  }

  // KEY_VALUE_SET will be executed before this, so anvatoIframe should have some values.
  if ('METADATA_LOADED' === eventDetail.name && eventDetail.pInstance === anvatoIframe?.id) {
    const anvatoPlayerData = window.anvp[anvatoIframe?.id].mergedConfig;
    const videoInfo = eventDetail.args[2];
    window.anvatoVideoAssetId = videoInfo.video;
    window.anvatoVideoTitle = videoInfo.title ?? anvatoPlayerData.openGraphInfo.title;
  }

  if ('KEY_VALUE_SET' === eventDetail.name) {
    const articleHeader = document.querySelector('.article-header');
    isVideoPage = 'video' === window.NXSTdata.content.pageType;
    anvatoWrapper = (isVideoPage || isHeroVideoFloat)
      ? document.querySelector('.video-playlist__player-intrinsic-sizer')
      : articleHeader?.querySelector('.ns-block-embed__wrapper');

    if (! isVideoPage && window.location.href.includes('newsnation')
      && ! anvatoWrapper) {
      anvatoWrapper = document.querySelector('.ns-block-embed__wrapper');
      /* eslint-disable-next-line no-unused-expressions */
      anvatoWrapper?.parentElement?.setAttribute('style', 'z-index: 4 !important');
    }
    anvatoIframe = anvatoWrapper?.querySelector('iframe');

    if (anvatoIframe?.id && ! window.anvatoVideoAssetId) {
      const anvatoPlayerData = window.anvp[anvatoIframe?.id]?.mergedConfig;
      window.anvatoVideoAssetId = anvatoPlayerData?.video;
      window.anvatoVideoTitle = anvatoPlayerData.title ?? anvatoPlayerData.openGraphInfo.title;
    }
    /* If anvato wrapper exists,
     * AND this is a video page, OR a page with the Daily News Hero on it OR an article page,
     * AND we're not on mobile OR this isn't the video page OR we're on newsnation,
     * AND we're not on newsnation OR the video in the player is undefined OR there are no ads
     * THEN add an event listener to the scrolling functionality to check whether the floating player is activated
    */
    if (anvatoWrapper
      && (isVideoPage || isHeroVideoFloat || 'article' === window.NXSTdata.content.pageType)
      && (! isMobile() || ! isVideoPage
      || window.location.href.includes('newsnation'))
      && (! window.location.href.includes('newsnation')
      || (window.anvp[anvatoIframe?.id]?.config?.video === undefined
      || ! window.anvp[anvatoIframe?.id]?.config?.video?.includes('ads')))) {
      document.addEventListener('scroll', checkFloatingPlayerActivation);
    }

    bannerSize = isMobile() ? 65 : 70;
    const isFloating = 'fixed' === anvatoWrapper?.style?.position;
    const rect = anvatoWrapper?.getBoundingClientRect();
    if (rect) {
      const isVisible = rect.top < window.innerHeight && 0 < rect.bottom;

      if (! isFloating && ! isVisible && firstTimeVisibilityCheck && playing) {
        makeAnvatoFloating(anvatoWrapper, anvatoIframe);
      }
    }
  } else if ('STATE_CHANGE' === eventDetail.name) {
    if (('pausedVideoContent' === eventDetail.args[0]
      || 'pausedAdContent' === eventDetail.args[0])
      && (isVideoPage || anvatoIframe?.id === eventDetail.pInstance)) {
      playing = false;
    } else if (('playingVideoContent' === eventDetail.args[0]
      || 'playingAdContent' === eventDetail.args[0])) {
      if (isVideoPage || anvatoIframe?.id === eventDetail.pInstance) {
        playing = true;
      } else {
        window.anvp[anvatoIframe?.id].pause();
        makeAnvatoFixed(anvatoWrapper, anvatoIframe, 'automatic');
      }
    } else if ('playerReady' === eventDetail.args[0]) {
      deleteTitleOverlay(anvatoWrapper, anvatoIframe);
    }
  } else if ('BUFFER_END' === eventDetail.name
    && anvatoIframe?.id === eventDetail.pInstance) {
    setTimeout(() => {
      playing = true;
    }, 250);
    const isFloating = 'static' === anvatoWrapper.style.position;
    const rect = anvatoWrapper.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && bannerSize < rect.bottom;

    if (! isFloating && ! isVisible) {
      makeAnvatoFloating(anvatoWrapper, anvatoIframe);
    }
  } else if ((isVideoPage || isHeroVideoFloat) && 'PLAYER_EXIT' === eventDetail.name) {
    makeAnvatoFixed(anvatoWrapper, anvatoIframe, 'automatic');
  }
}

/**
 * Deletes title overlay of Anvato if the video is in lead media.
 *
 * @param {Element} wrapper Anvato player's wrapper on the page
 * @param {Element} iframe Anvato iframe displaying the video
 *
 */
function deleteTitleOverlay(wrapper, iframe) {
  while (wrapper) {
    if (wrapper.classList.contains('article-header')) {
      iframe.contentWindow.document.querySelector('.anv-splash-title')
        .style.display = 'none';
    }
    wrapper = wrapper.parentElement;
  }
}

/**
 * add the content lenght duration to the adTagUrl
 *
 * @param  {object} playerConfig Anvato player config
 * @return {object} Updated player config
 *
 */
function addVideoContentLength(playerConfig, mediaInfo) {
  // Detect if there is any blocker running on the page
  detectAnyAdblocker().then((detected) => {
    if (detected) {
      const adBlockerTracker = new SimpleErrorTracker('Player detected content blocker', 100, {
        id: playerConfig?.pInstance,
        position: playerConfig?.plugins?.segmentCustom?.playerName,
        renderGroup: 'Akta internal',
      });
      adBlockerTracker.sendSuccess();
    }
  });

  let withDurationURL;
  let iuURL;
  // Add bids to ad tag url
  const { nexstarA9, nexstarPrebid, nexstarHtlbid } = window;
  const bids = {
    ...nexstarA9?.[playerConfig?.pInstance]?.bids,
    ...nexstarPrebid?.[playerConfig?.pInstance]?.bids,
    ...nexstarHtlbid?.[playerConfig?.pInstance]?.bids,
  };
  try {
    if (playerConfig.plugins?.dfp?.clientSide?.isFreewheel) {
      iuURL = updateFreewheelUrl(
        playerConfig.plugins.dfp.clientSide.adTagUrl,
        {
          videoId: mediaInfo?.id,
          vdur: mediaInfo?.duration,
          _fw_content_language: mediaInfo?.language,
        },
      );
    } else {
      withDurationURL = custParamsUrl(
        { vid: playerConfig.video, ...bids, content_len: mediaInfo?.duration },
        new URL(playerConfig.plugins.dfp.clientSide.adTagUrl),
      );
      iuURL = new URL(withDurationURL);
    }
  } catch (e) {
    return playerConfig;
  }

  const newConfig = { ...playerConfig };
  newConfig.plugins.dfp.clientSide.adTagUrl = iuURL.toString();
  return newConfig;
}

window.addEventListener('nsAnvatoPlayerEvent',
  (e) => {
    eventHandler(e.detail);
  });
